import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";

interface AdminRecordActionsProps {
  /** Optional edit handler — the pencil is only shown when provided. */
  onEdit?: () => void;
  /** Delete handler, fired only after the user confirms. */
  onDelete: () => void;
  /** Human name of the record, used in the confirm prompt (e.g. "el gasto"). */
  entityLabel?: string;
  disabled?: boolean;
  /** When true, render icon-only buttons (for tight table rows). */
  compact?: boolean;
  className?: string;
}

/**
 * Admin-only Edit + Delete controls for a record row/card.
 *
 * Renders nothing for non-admins — the real security boundary is the API, but
 * hiding the controls keeps the UI honest about who may correct or remove data.
 * Delete always asks for confirmation first. Every deletion is recorded in the
 * audit tally server-side (see the Audit Log "solo eliminaciones" filter).
 */
export function AdminRecordActions({
  onEdit,
  onDelete,
  entityLabel,
  disabled,
  compact,
  className,
}: AdminRecordActionsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  if (user?.role !== "ADMIN") return null;

  const confirmMsg = entityLabel
    ? t("common.confirmDeleteNamed", { entity: entityLabel })
    : t("common.confirmDelete");

  return (
    <div className={`inline-flex items-center gap-3 ${className ?? ""}`}>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          disabled={disabled}
          className="text-slate-600 hover:text-primary text-sm inline-flex items-center gap-1 disabled:opacity-50"
          title={t("common.edit")}
        >
          <Pencil size={14} />
          {!compact && <span>{t("common.edit")}</span>}
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          if (confirm(confirmMsg)) onDelete();
        }}
        disabled={disabled}
        className="text-red-600 hover:text-red-700 text-sm inline-flex items-center gap-1 disabled:opacity-50"
        title={t("common.delete")}
      >
        <Trash2 size={14} />
        {!compact && <span>{t("common.delete")}</span>}
      </button>
    </div>
  );
}
